import React, { useState, useEffect } from "react";
import "./index.css";
import { useParams } from "react-router-dom";

const MCQPractice = () => {
  const { id } = useParams();

  const [mcqQuestions, setMcqQuestions] = useState([]);

  useEffect(() => {
    const fetchMCQ = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/api/mcqs`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ id }),
          }
        );

        const data = await response.json();
        setMcqQuestions(data);
      } catch (error) {
        throw new Error();
      }
    };

    fetchMCQ();
  }, []);

  // Fisher-Yates shuffle algorithm
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Shuffle questions AND options while maintaining correct answer mapping
  const shuffleQuestionsAndOptions = (questions) => {
    // First shuffle the questions array
    const shuffledQuestions = shuffleArray(questions);

    // Then shuffle options for each question and update correctAnswer index
    return shuffledQuestions.map((question) => {
      // Create pairs of options with their original indices
      const optionsWithIndices = question.options.map((option, index) => ({
        option,
        originalIndex: index,
      }));

      // Shuffle the options with their indices
      const shuffledOptionsWithIndices = shuffleArray(optionsWithIndices);

      // Extract just the option texts for the new question
      const newOptions = shuffledOptionsWithIndices.map((item) => item.option);

      // Find the new index of the correct answer
      const newCorrectAnswer = shuffledOptionsWithIndices.findIndex(
        (item) => item.originalIndex === question.correctAnswer
      );

      return {
        ...question,
        options: newOptions,
        correctAnswer: newCorrectAnswer,
      };
    });
  };

  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [answeredQuestions, setAnsweredQuestions] = useState({});

  // Initialize with shuffled questions and options
  useEffect(() => {
    if (mcqQuestions.length > 0) {
      const shuffledQuestions = shuffleQuestionsAndOptions(mcqQuestions);
      setQuestions(shuffledQuestions);
    }
  }, [mcqQuestions]);

  const currentQuestion = questions[currentQuestionIndex];

  // Fixed Timer effect
  useEffect(() => {
    let timer;
    if (isTimerRunning && !quizCompleted) {
      timer = setInterval(() => {
        setTimeSpent((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isTimerRunning, quizCompleted]);

  // Check if current question was already answered when navigating
  useEffect(() => {
    if (currentQuestionIndex in answeredQuestions) {
      setSelectedAnswer(answeredQuestions[currentQuestionIndex]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestionIndex, answeredQuestions]);

  const handleAnswerSelect = (optionIndex) => {
    if (selectedAnswer !== null || showExplanation) return;

    setSelectedAnswer(optionIndex);
    setShowExplanation(true);

    // Update score if correct (only if not already answered)
    if (!(currentQuestionIndex in answeredQuestions)) {
      if (optionIndex === currentQuestion.correctAnswer) {
        setScore((prev) => prev + 1);
      }
    }

    // Mark question as answered with the selected option
    setAnsweredQuestions((prev) => ({
      ...prev,
      [currentQuestionIndex]: optionIndex,
    }));
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleQuizCompletion();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleQuizCompletion = () => {
    setIsTimerRunning(false);
    setQuizCompleted(true);
  };

  const handleRestartQuiz = () => {
    // Shuffle both questions and options for every new attempt
    const shuffledQuestions = shuffleQuestionsAndOptions(mcqQuestions);
    setQuestions(shuffledQuestions);
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setQuizCompleted(false);
    setTimeSpent(0);
    setAnsweredQuestions({});
    setIsTimerRunning(true);
  };

  const handleJumpToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Function to get the original option letter (A, B, C, D) for display
  const getOptionLetter = (index) => {
    return String.fromCharCode(65 + index);
  };

  if (quizCompleted) {
    return (
      <div className="mcq-container">
        <div className="quiz-completed">
          <div className="completion-card">
            <h2>Test Completed! </h2>
            <div className="score-section">
              <h3>Your Score</h3>
              <div className="score-circle">
                <span className="score">{score}</span>
                <span className="total">/{questions.length}</span>
              </div>
              <p className="percentage">
                {Math.round((score / questions.length) * 100)}%
              </p>
            </div>
            <div className="stats">
              <div className="stat">
                <span className="stat-label">Time Spent</span>
                <span className="stat-value">{formatTime(timeSpent)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Correct Answers</span>
                <span className="stat-value">{score}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Incorrect Answers</span>
                <span className="stat-value">{questions.length - score}</span>
              </div>
            </div>
            <button className="restart-btn" onClick={handleRestartQuiz}>
              Start New Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return <div className="mcq-container loading">Loading quiz...</div>;
  }

  return (
    <div className="mcq-container">
      <div className="question-section">
        <h2 className="question-text">{currentQuestion.question}</h2>
        <div className="options-grid">
          {currentQuestion.options.map((option, index) => {
            const isSelected = selectedAnswer === index;
            const isCorrect = index === currentQuestion.correctAnswer;
            let optionClass = "option-btn";

            if (showExplanation) {
              if (isCorrect) {
                optionClass += " correct";
              } else if (isSelected && !isCorrect) {
                optionClass += " incorrect";
              }
            } else if (isSelected) {
              optionClass += " selected";
            }

            return (
              <button
                key={index}
                className={optionClass}
                onClick={() => handleAnswerSelect(index)}
                disabled={
                  showExplanation || currentQuestionIndex in answeredQuestions
                }
              >
                <span className="option-letter">{getOptionLetter(index)}</span>
                <span className="option-text">{option}</span>
                {showExplanation && isCorrect && (
                  <span className="checkmark">✓</span>
                )}
                {showExplanation && isSelected && !isCorrect && (
                  <span className="crossmark">✗</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {showExplanation && (
        <div className="explanation-section">
          <h4>Explanation</h4>
          <p>{currentQuestion.explanation}</p>
          <button className="next-explanation-btn" onClick={handleNextQuestion}>
            {currentQuestionIndex === questions.length - 1
              ? "Finish Quiz"
              : "Next Question →"}
          </button>
        </div>
      )}

      <div className="navigation-section">
        <button
          className="nav-btn prev-btn"
          onClick={handlePreviousQuestion}
          disabled={currentQuestionIndex === 0}
        >
          ← Previous
        </button>

        <div className="question-dots">
          {questions.map((_, index) => {
            const isAnswered = index in answeredQuestions;
            const isCorrect =
              isAnswered &&
              answeredQuestions[index] === questions[index].correctAnswer;

            let dotClass = "dot";
            if (index === currentQuestionIndex) {
              dotClass += " active";
            }
            if (isAnswered) {
              dotClass += isCorrect ? " correct-dot" : " incorrect-dot";
            }

            return (
              <button
                key={index}
                className={dotClass}
                onClick={() => handleJumpToQuestion(index)}
                title={`Question ${index + 1}${
                  isAnswered ? " (Answered)" : ""
                }`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        {!showExplanation && (
          <button className="nav-btn next-btn" onClick={handleNextQuestion}>
            {currentQuestionIndex === questions.length - 1
              ? "Finish Quiz"
              : "Next →"}
          </button>
        )}
      </div>
    </div>
  );
};

export default MCQPractice;
